import { useEffect, useRef } from 'react';

/** Where the card view is: shortcuts mean different things at each stage. */
export type ShortcutPhase = 'off' | 'intro' | 'card';

/** Sent to the card's player (`[data-shortcut-player]`) for R and S. */
export const SHORTCUT_EVENT = 'turncard-shortcut';
export type PlayerShortcut = 'again' | 'slower';

interface Options {
  phase: ShortcutPhase;
  /** The learner is recording or an attempt is being checked: R and S stay quiet. */
  busy: boolean;
  onPause: () => void;
}

const TEXT_FIELDS = 'input, textarea, select, [contenteditable]';

/**
 * Keyboard shortcuts for the turn card (#307): Space speaks (Start at the intro), R plays the phrase
 * again, S slower, Esc pauses. The hook never plays or records anything: it clicks the real control
 * (or tells the card's player), so the audio and mic rules in `speech/` and `awaitingInput` still
 * decide what happens. Keys are read by position (`code`) so a Greek layout works.
 */
export function useTurnShortcuts({ phase, busy, onPause }: Options): void {
  const latest = useRef({ phase, busy, onPause });
  latest.current = { phase, busy, onPause };

  useEffect(() => {
    let swallowSpaceUp = false;
    // :focus-visible can't tell the two apart here (the keydown itself switches it to keyboard).
    let pointerFocus = false;
    const onPointerDown = (): void => {
      pointerFocus = true;
    };
    const onKeyUp = (e: KeyboardEvent): void => {
      if (e.code !== 'Space' || !swallowSpaceUp) return;
      swallowSpaceUp = false;
      e.preventDefault();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Tab') pointerFocus = false;
      const { phase: p, busy: isBusy, onPause: pause } = latest.current;
      if (p === 'off' || e.repeat) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const target = e.target instanceof Element ? e.target : null;
      if (target?.closest(`${TEXT_FIELDS}, [role="dialog"]`)) return;

      if (e.key === 'Escape') {
        if (p !== 'card') return;
        e.preventDefault();
        pause();
        return;
      }
      if (e.code === 'Space') {
        // A button or link the learner reached by keyboard keeps its own Space. One they clicked does not:
        // after tapping Play, Space should still mean speak.
        if (!pointerFocus && target?.closest('button, a[href]')) return;
        const control = document.querySelector<HTMLButtonElement>(
          `[data-shortcut="${p === 'intro' ? 'start' : 'speak'}"]`
        );
        e.preventDefault();
        // A button that has focus would otherwise also click on the key's release.
        swallowSpaceUp = true;
        if (control && !control.disabled) control.click();
        return;
      }
      const action: PlayerShortcut | null =
        e.code === 'KeyR' ? 'again' : e.code === 'KeyS' ? 'slower' : null;
      if (!action || p !== 'card' || isBusy) return;
      document
        .querySelector('[data-shortcut-player]')
        ?.dispatchEvent(new CustomEvent(SHORTCUT_EVENT, { detail: action }));
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKey);
    document.addEventListener('keyup', onKeyUp);
    return (): void => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('keyup', onKeyUp);
    };
  }, []);
}
